import { useTranslation } from 'react-i18next';
import useAlerts from './useAlerts';
import { useNavigate } from 'react-router-dom';
import useRoutePrefix from '../../../hooks/useRoutePrefix';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { enqueueSnackbar } from 'notistack';
import { showAlert } from '../../../features/slices/applicationSlice';
import { ALERT_CONFIRM_TEXT, ERROR_CONTACT_SUPPORT } from '../../../constants/values';
import {
    useEditCarcinogenicReportEstablishmentMutation,
    useGetCarcinogenicSrtValidacionesQuery
} from '../../../services/carcinogenicApi';

const useSendCarcinogenicReport = ({
    isReadyToPresent,
    faltantes = []
}: {
    isReadyToPresent: boolean;
    faltantes?: string[];
}) => {
    const dispatch = useAppDispatch();
    const routePrefix = useRoutePrefix();
    const establishmentData = useAppSelector(
        (state) => state?.carcinogenicReport?.carcinogenicReport?.establecimiento
    );

    const selectedEstablishmentId = useAppSelector(
        (state) => state.establishment.selectedEstablishmentId
    );

    const selectedCarcinogenicReportId = useAppSelector(
        (state) => state?.carcinogenicReport?.selectedCarcinogenicReportId
    );

    const providersEmptyDeclarationCheckState = useAppSelector(
        (state) => state?.carcinogenicReport?.carcinogenicReport?.proveedoresCheck
    );

        const radiologicalEmptyDeclarationCheckState = useAppSelector(
            (state) => state?.carcinogenicReport?.carcinogenicReport?.radiologicosCheck
        );

    const [editCarcinogenicReport, { isLoading: isEditingCarcinogenicReport }] =
        useEditCarcinogenicReportEstablishmentMutation();

    const navigate = useNavigate();
    const { t } = useTranslation('carcinogenicReport');
    const {
        showSendCarcinogenicReportAlert,
        showSendCarcinogenicReportFailAlert,
        showSendBlockedByCorrectionsAlert
    } = useAlerts({
        translate: t
    });

    // Las mismas reglas que muestra el panel de correcciones (misma query, sale
    // de caché): con bloqueantes la SRT rechazaría el informe, así que no se
    // envía. Si la validación falla no se traba el envío, igual que el panel.
    const { data: validaciones } = useGetCarcinogenicSrtValidacionesQuery(
        selectedCarcinogenicReportId,
        { skip: !selectedCarcinogenicReportId }
    );
    const bloqueantes = (validaciones?.data?.errores ?? []).filter(
        (e: { severidad?: string }) => e.severidad === 'error'
    ).length;

    const handleSendCarcinogenicReportClickButtonClick = () => {
        if (!isReadyToPresent) {
            showSendCarcinogenicReportFailAlert(faltantes);
            return;
        }
        if (bloqueantes > 0) {
            showSendBlockedByCorrectionsAlert(bloqueantes);
            return;
        }
        showSendCarcinogenicReportAlert({ confirmAction: handleSendCarcinogenicReport });
    };

    const handleSendCarcinogenicReport = async () => {
        try {
            if (!selectedCarcinogenicReportId || !selectedEstablishmentId) return;

            const response: any = await editCarcinogenicReport({
                reportId: selectedCarcinogenicReportId,
                body: {
                    idEstablecimiento: selectedEstablishmentId,
                    establecimiento: establishmentData,
                    proveedoresCheck: providersEmptyDeclarationCheckState,
                    radiologicosCheck: radiologicalEmptyDeclarationCheckState
                },
                isBorrador: false
            });

            if (response?.error) {
                showSendCarcinogenicReportFailAlert();
            } else {
                enqueueSnackbar(t('enqueues.sendPresentation.success'), {
                    variant: 'success'
                });
                /*
                 * A Presentaciones del establecimiento, no `navigate(-1)`: "atrás"
                 * es lo último que el usuario abrió antes del informe, que puede
                 * ser cualquier pantalla (Nómina, si fue a revisar un empleado).
                 */
                navigate(`/${routePrefix}/establecimiento/presentaciones`);
            }
        } catch (error) {
            dispatch(
                showAlert({
                    title: 'No se pudo enviar el informe de cancerígenos',
                    message: ERROR_CONTACT_SUPPORT,
                    icon: 'error',
                    confirmText: ALERT_CONFIRM_TEXT
                })
            );
        }
    };

    return { handleSendCarcinogenicReportClickButtonClick, isEditingCarcinogenicReport };
};

export default useSendCarcinogenicReport;
